import { Camera, ImagePlus, X } from "lucide-react";
import { Dialog } from "radix-ui";
import { type ChangeEvent, type ReactNode, useEffect, useRef, useState } from "react";

import { Button } from "@/components/ui/button";
import { LIMITES } from "@/features/pedidos/imagenes";
import { cn } from "@/lib/cn";

import { reducirFoto } from "./reducir";

/**
 * **«Adjuntar foto»: el único selector de fotos del ERP** (ronda 20-oct, `c-fotos`). Dos caminos:
 * - **Elegir archivo**: la foto guardada en el celular o en una carpeta.
 * - **Tomar foto**: en el celular abre la cámara trasera (`capture="environment"`); en el computador, la
 *   cámara web con permiso (`getUserMedia`). Si el permiso se niega o no hay cámara, lo dice y deja solo
 *   «Elegir archivo».
 *
 * Toda foto se reduce antes de entregarla (`reducir.ts`: 2000 px, JPEG o WebP). Cada pantalla recibe un
 * `File` y la sube como siempre: **no cambian las rutas ni los campos que se envían**, ni sus revisiones
 * (`revisarArchivo`), que siguen en cada pantalla.
 *
 * Dos formas de usarlo: `<AdjuntarFoto>` pinta los dos botones donde hay sitio; `useAdjuntarFoto` da
 * `abrir()` para el botón pequeño que ya tenía cada tarjeta o menú (abre una ventana con los dos
 * caminos) o `elegir()`/`tomar()` directos. Las `piezas` (entradas ocultas y la ventana) van siempre.
 */

type Paso = "cerrado" | "opciones" | "camara" | "sin-camara";

/** ¿Celular o tableta? Ahí la cámara la abre el propio selector con `capture`. */
const esTactil = () => typeof window.matchMedia === "function" && window.matchMedia("(pointer: coarse)").matches;
const hayCamaraWeb = () => typeof navigator !== "undefined" && typeof navigator.mediaDevices?.getUserMedia === "function";

export function useAdjuntarFoto({ onFoto, etiqueta, ladoMayor }: {
  /** La foto ya reducida, lista para revisar y subir como siempre. */
  onFoto: (foto: File) => void;
  /** El nombre accesible de la entrada de archivo (el de siempre de cada pantalla). */
  etiqueta: string;
  /** El lado mayor de la foto; por omisión 2000 px. */
  ladoMayor?: number;
}) {
  const archivo = useRef<HTMLInputElement>(null);
  const camara = useRef<HTMLInputElement>(null);
  const video = useRef<HTMLVideoElement>(null);
  const flujo = useRef<MediaStream | null>(null);
  const [paso, setPaso] = useState<Paso>("cerrado");
  const [preparando, setPreparando] = useState(false);
  const [negada, setNegada] = useState(false);

  const apagar = () => {
    flujo.current?.getTracks().forEach((t) => t.stop());
    flujo.current = null;
  };
  useEffect(() => apagar, []);
  // El video se conecta cuando la ventana ya lo pintó.
  useEffect(() => {
    if (paso === "camara" && video.current && flujo.current) {
      video.current.srcObject = flujo.current;
      void video.current.play?.()?.catch(() => null);
    }
  }, [paso]);

  const cerrar = () => {
    apagar();
    setPaso("cerrado");
  };

  async function entregar(foto: File | null | undefined) {
    if (!foto) return;
    cerrar();
    setPreparando(true);
    try {
      onFoto(await reducirFoto(foto, { ladoMayor }));
    } finally {
      setPreparando(false);
    }
  }

  const elegir = () => archivo.current?.click();

  async function tomar() {
    if (esTactil() || !hayCamaraWeb()) {
      camara.current?.click();
      return;
    }
    try {
      flujo.current = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment", width: { ideal: 1920 } }, audio: false });
      setPaso("camara");
    } catch {
      setNegada(true);
      setPaso("sin-camara");
    }
  }

  async function capturar() {
    const v = video.current;
    if (!v || !v.videoWidth) return;
    const lienzo = document.createElement("canvas");
    lienzo.width = v.videoWidth;
    lienzo.height = v.videoHeight;
    lienzo.getContext("2d")?.drawImage(v, 0, 0);
    const blob = await new Promise<Blob | null>((ok) => lienzo.toBlob(ok, "image/jpeg", 0.92));
    if (blob) await entregar(new File([blob], `foto-${Date.now()}.jpg`, { type: "image/jpeg" }));
  }

  const alElegir = (e: ChangeEvent<HTMLInputElement>) => {
    const foto = e.target.files?.[0];
    // Se limpia para que elegir DOS VECES el mismo archivo vuelva a disparar el cambio.
    e.target.value = "";
    void entregar(foto);
  };

  const piezas = (
    <>
      <input ref={archivo} type="file" accept={LIMITES.tipos.join(",")} aria-label={etiqueta} className="sr-only" tabIndex={-1} onChange={alElegir} />
      <input ref={camara} type="file" accept="image/*" capture="environment" aria-label={`${etiqueta} · cámara`} className="sr-only" tabIndex={-1} onChange={alElegir} />
      <Dialog.Root open={paso !== "cerrado"} onOpenChange={(abierta) => (abierta ? null : cerrar())}>
        <Dialog.Portal>
          <Dialog.Overlay data-adjuntar-foto="" className="fixed inset-0 z-50 bg-black/60" />
          <Dialog.Content data-adjuntar-foto=""
            className="fixed left-1/2 top-1/2 z-50 grid w-[min(92vw,560px)] -translate-x-1/2 -translate-y-1/2 gap-4 rounded-lg bg-surface p-5 text-ink shadow-card">
            <div className="flex items-start justify-between gap-3">
              <Dialog.Title className="text-lg font-bold">Adjuntar foto</Dialog.Title>
              <Dialog.Close className="inline-flex size-9 items-center justify-center rounded-md text-ink-2 hover:bg-surface-2" aria-label="Cerrar">
                <X aria-hidden className="size-5" />
              </Dialog.Close>
            </div>
            <Dialog.Description className="sr-only">{etiqueta}</Dialog.Description>
            {paso === "camara" ? (
              <>
                <video ref={video} autoPlay playsInline muted aria-label="Vista de la cámara" className="max-h-[60vh] w-full rounded-md bg-black object-contain" />
                <div className="flex flex-wrap justify-end gap-2">
                  <Button type="button" variant="secondary" onClick={() => { apagar(); setPaso("opciones"); }}>Volver</Button>
                  <Button type="button" onClick={() => void capturar()}><Camera aria-hidden />Tomar</Button>
                </div>
              </>
            ) : (
              <>
                {paso === "sin-camara" ? (
                  <p role="alert" className="text-sm text-bad">No se pudo abrir la cámara (permiso negado o no hay cámara). Elige un archivo.</p>
                ) : null}
                <Botones elegir={elegir} tomar={paso === "sin-camara" || negada ? null : () => void tomar()} />
              </>
            )}
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </>
  );

  return {
    /** Abre la ventana con «Elegir archivo» y «Tomar foto» (para el botón pequeño de una tarjeta o un menú). */
    abrir: () => setPaso("opciones"),
    elegir,
    tomar: () => void tomar(),
    /** La foto se está reduciendo (un momento, en fotos grandes). */
    preparando,
    /** Se negó la cámara en esta pantalla: solo queda «Elegir archivo». */
    negada,
    piezas,
  };
}

function Botones({ elegir, tomar, className }: { elegir: () => void; tomar: (() => void) | null; className?: string }) {
  return (
    <div className={cn("flex flex-wrap gap-2", className)}>
      <Button type="button" variant="secondary" onClick={elegir}><ImagePlus aria-hidden />Elegir archivo</Button>
      {tomar ? <Button type="button" variant="secondary" onClick={tomar}><Camera aria-hidden />Tomar foto</Button> : null}
    </div>
  );
}

/** Los dos botones a la vista, para donde hay sitio (una ficha, un formulario). */
export function AdjuntarFoto({ onFoto, etiqueta, ladoMayor, disabled = false, className, children }: {
  onFoto: (foto: File) => void;
  etiqueta: string;
  ladoMayor?: number;
  disabled?: boolean;
  className?: string;
  /** Lo que va antes de los botones (un título corto). */
  children?: ReactNode;
}) {
  const foto = useAdjuntarFoto({ onFoto, etiqueta, ladoMayor });
  return (
    <div className={cn("grid gap-1.5", className)}>
      {children}
      <fieldset disabled={disabled || foto.preparando} className="contents">
        <Botones elegir={foto.elegir} tomar={foto.negada ? null : foto.tomar} />
      </fieldset>
      {foto.preparando ? <p role="status" className="text-xs text-ink-3">Preparando la foto…</p> : null}
      {foto.piezas}
    </div>
  );
}
